import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export type AnnouncementTrigger = 'login' | 'exam' | 'tab';
export type AnnouncementDisplayMode = 'fullscreen' | 'corner';
export type CornerPosition = 'bottom-right' | 'top-right' | 'bottom-left' | 'top-left';
export type DismissReason = 'dontShowAgain' | 'close' | 'cta';

export interface AnnouncementBubble {
  id: number;
  titleHe: string | null;
  titleEn: string | null;
  bodyHe: string | null;
  bodyEn: string | null;
  bodyHeDir: 'rtl' | 'ltr';
  bodyEnDir: 'rtl' | 'ltr';
  imageUrl: string | null;
  ctaTextHe: string | null;
  ctaTextEn: string | null;
  ctaUrl: string | null;
  dontShowAgainTextHe: string;
  dontShowAgainTextEn: string;
  displayMode: AnnouncementDisplayMode;
  backgroundColor: string | null;
  textColor: string | null;
  cornerPosition: CornerPosition;
  cornerWidthPercent: number;
  cornerHeightPercent: number | null;
  priority: number;
  allowDontShowAgain: boolean;
  closeDismissesForever: boolean;
}

// Read side of the UMT-managed popup announcements ("bubbles"). Every client
// (aweb, awebCardio, gastroAweb, eyes-aweb, usgaweb, awebAppointments) carries
// an identical copy of this service — the backend already resolves system,
// clinic, feature and stop-rule targeting, so there's nothing product-specific
// to do here.
@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  constructor(private http: HttpClient) {}

  // tabKey is this product's own tab identifier (e.g. 'Anatomy', 'Calendar')
  // — required only when trigger is 'tab'.
  fetch(trigger: AnnouncementTrigger, tabKey?: string): Observable<AnnouncementBubble[]> {
    const params: Record<string, string> = { trigger };
    if (tabKey) params['tabKey'] = tabKey;
    return this.http.get<AnnouncementBubble[]>('/api/announcements', { params });
  }

  recordView(id: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`/api/announcements/${id}/view`, {});
  }

  dismiss(id: number, reason: DismissReason): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`/api/announcements/${id}/dismiss`, { reason });
  }
}
